import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, CheckCircle, XCircle, RotateCcw, Trophy, Sparkles, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuizPage() {
  const { moduleId } = useParams();
  const { language, t } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [adaptiveMode, setAdaptiveMode] = useState(false);
  const [userLevel, setUserLevel] = useState(null);

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        // Check user's quiz level first
        const levelRes = await axios.get(`${API}/user/quiz-level`, { headers: getAuthHeaders(), withCredentials: true }).catch(() => null);
        if (levelRes?.data) setUserLevel(levelRes.data);
        
        const res = await axios.get(`${API}/quizzes/${moduleId}`);
        setQuiz(res.data);
      } catch (err) {
        console.error('Quiz fetch error:', err);
      }
      setLoading(false);
    };
    fetchQuiz();
  }, [moduleId, getAuthHeaders]);

  const switchToAdaptive = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quizzes/${moduleId}/adaptive`, { headers: getAuthHeaders(), withCredentials: true });
      setQuiz(res.data);
      setAdaptiveMode(true);
      setAnswers({});
      setCurrentQ(0);
      setResults(null);
      if (res.data.user_level) setUserLevel(prev => ({ ...prev, level: res.data.user_level }));
    } catch (err) {
      console.error('Adaptive quiz error:', err);
    }
    setLoading(false);
  };

  const switchToStandard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quizzes/${moduleId}`);
      setQuiz(res.data);
      setAdaptiveMode(false);
      setAnswers({});
      setCurrentQ(0);
      setResults(null);
    } catch (err) {
      console.error('Standard quiz error:', err);
    }
    setLoading(false);
  };

  const handleAnswer = (qIndex, aIndex) => {
    setAnswers(prev => ({ ...prev, [String(qIndex)]: aIndex }));
  };

  const handleSubmit = async () => {
    if (!quiz) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quizzes/submit`, {
        quiz_id: quiz.quiz_id,
        answers
      }, { headers: getAuthHeaders(), withCredentials: true });
      setResults(res.data);
    } catch (err) {
      console.error('Submit error:', err);
    }
    setSubmitting(false);
  };

  const handleRetry = () => {
    setAnswers({});
    setResults(null);
    setCurrentQ(0);
  };

  if (loading || !quiz) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const questions = quiz.questions;
  const totalQuestions = questions.length;
  const answered = Object.keys(answers).length;

  // Results view
  if (results) {
    const { score, correct, total } = results;
    const passed = score >= 70;
    const levelLabels = { beginner: t('quiz_level_beginner'), intermediate: t('quiz_level_intermediate'), advanced: t('quiz_level_advanced') };

    return (
      <div className="min-h-screen bg-[#E8ECF1]" data-testid="quiz-results-page">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Score Card */}
          <div className={`rounded-2xl p-8 text-center mb-8 ${passed ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="quiz-score-card">
            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${passed ? 'bg-emerald-100' : 'bg-amber-100'}`}>
              <Trophy className={`w-8 h-8 ${passed ? 'text-emerald-600' : 'text-amber-600'}`} />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Poppins' }}>
              {t('quiz_score')}: {score}%
            </h2>
            <p className="text-slate-600">{correct}/{total} {language === 'fr' ? 'réponses correctes' : 'correct answers'}</p>
            {adaptiveMode && userLevel && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200">
                <Zap className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-sm text-blue-700 font-medium">
                  {t('quiz_level')}: {levelLabels[userLevel.level] || userLevel.level}
                </span>
              </div>
            )}
            {/* Level feedback */}
            {adaptiveMode && (
              <p className="mt-3 text-sm text-slate-500">
                {score >= 80
                  ? (language === 'fr' ? 'Excellent ! Vous progressez vers le niveau supérieur.' : 'Excellent! You are progressing to the next level.')
                  : score >= 50
                  ? (language === 'fr' ? 'Bon travail ! Continuez à ce rythme.' : 'Good work! Keep up this pace.')
                  : (language === 'fr' ? 'Continuez à réviser les bases. Vous allez y arriver !' : 'Keep reviewing the basics. You\'ll get there!')}
              </p>
            )}
          </div>

          {/* Results Detail */}
          <div className="space-y-4 mb-8">
            {results.results.map((r, i) => {
              const q = questions[i];
              return (
                <div key={i} className={`bg-white rounded-xl border p-5 ${r.is_correct ? 'border-emerald-200' : 'border-red-200'}`} data-testid={`quiz-result-${i}`}>
                  <div className="flex items-start gap-3">
                    {r.is_correct ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-slate-900 mb-2">
                        {language === 'fr' ? q.question_fr : q.question_en}
                      </p>
                      <p className={`text-sm ${r.is_correct ? 'text-emerald-600' : 'text-red-600'}`}>
                        {r.is_correct ? t('quiz_correct') : t('quiz_wrong')}
                      </p>
                      {!r.is_correct && (
                        <p className="text-sm text-slate-500 mt-2 bg-slate-50 p-2 rounded">
                          <span className="font-medium">{t('quiz_explanation')}:</span>{' '}
                          {language === 'fr' ? r.explanation_fr : r.explanation_en}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleRetry} data-testid="quiz-retry-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> {t('quiz_retry')}
            </Button>
            <Button className="bg-blue-600 text-white" onClick={() => navigate(`/course/${moduleId}`)} data-testid="quiz-back-btn">
              {t('quiz_back')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Quiz view
  const q = questions[currentQ];

  return (
    <div className="min-h-screen bg-[#E8ECF1]" data-testid="quiz-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" className="mb-6 text-slate-600" onClick={() => navigate(`/course/${moduleId}`)} data-testid="quiz-back-to-course">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('quiz_back')}
        </Button>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-8 h-8 rounded-lg" data-testid="quiz-logo" />
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }} data-testid="quiz-title">
              {language === 'fr' ? quiz.title_fr : quiz.title_en}
            </h1>
          </div>
          
          {/* Adaptive Mode Toggle */}
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <span className="text-sm text-slate-500">
              {t('quiz_question')} {currentQ + 1} {t('quiz_of')} {totalQuestions}
            </span>
            <Badge variant="outline" className="text-xs">{answered}/{totalQuestions} {language === 'fr' ? 'répondues' : 'answered'}</Badge>
            
            <div className="ml-auto flex items-center gap-2">
              {userLevel && (
                <Badge className={`text-xs ${
                  userLevel.level === 'advanced' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                  userLevel.level === 'intermediate' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                  'bg-green-50 text-green-700 border-green-200'
                }`} data-testid="quiz-user-level">
                  <Zap className="w-3 h-3 mr-1" />
                  {userLevel.level === 'advanced' ? t('quiz_level_advanced') : userLevel.level === 'intermediate' ? t('quiz_level_intermediate') : t('quiz_level_beginner')}
                </Badge>
              )}
              <button
                onClick={adaptiveMode ? switchToStandard : switchToAdaptive}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  adaptiveMode
                    ? 'bg-orange-100 text-orange-700 border border-orange-200 hover:bg-orange-200'
                    : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
                }`}
                data-testid="quiz-adaptive-toggle"
              >
                <Sparkles className="w-3 h-3" />
                {adaptiveMode ? t('quiz_adaptive_mode') : t('quiz_standard_mode')}
              </button>
            </div>
          </div>
          
          <Progress value={(answered / totalQuestions) * 100} className="h-1.5 mt-3" />
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid={`quiz-question-${currentQ}`}>
          {/* Difficulty indicator */}
          {q.difficulty && (
            <div className="mb-3">
              <Badge className={`text-[10px] ${
                q.difficulty === 'advanced' ? 'bg-purple-50 text-purple-600 border-purple-200' :
                q.difficulty === 'intermediate' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                'bg-green-50 text-green-600 border-green-200'
              }`} data-testid={`quiz-difficulty-${currentQ}`}>
                {q.difficulty === 'advanced' ? t('quiz_level_advanced') : q.difficulty === 'intermediate' ? t('quiz_level_intermediate') : t('quiz_level_beginner')}
              </Badge>
            </div>
          )}
          <h2 className="text-lg font-semibold text-slate-900 mb-5">
            {language === 'fr' ? q.question_fr : q.question_en}
          </h2>
          <div className="space-y-3">
            {(language === 'fr' ? q.options_fr : q.options_en).map((option, oIdx) => {
              const isSelected = answers[String(currentQ)] === oIdx;
              return (
                <button
                  key={oIdx}
                  onClick={() => handleAnswer(currentQ, oIdx)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                    isSelected
                      ? 'border-blue-600 bg-blue-50 text-blue-900'
                      : 'border-slate-200 hover:border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid={`quiz-option-${currentQ}-${oIdx}`}
                >
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full mr-3 text-sm font-medium ${
                    isSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {String.fromCharCode(65 + oIdx)}
                  </span>
                  {option}
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            disabled={currentQ === 0}
            onClick={() => setCurrentQ(prev => prev - 1)}
            data-testid="quiz-prev-btn"
          >
            {t('course_prev')}
          </Button>
          <div className="flex gap-1">
            {questions.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentQ(i)}
                className={`w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                  i === currentQ
                    ? 'bg-blue-600 text-white'
                    : answers[String(i)] !== undefined
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-slate-100 text-slate-400'
                }`}
                data-testid={`quiz-nav-${i}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
          {currentQ < totalQuestions - 1 ? (
            <Button variant="ghost" onClick={() => setCurrentQ(prev => prev + 1)} data-testid="quiz-next-btn">
              {t('course_next')}
            </Button>
          ) : (
            <Button
              className="bg-blue-600 text-white active:scale-95 transition-transform"
              disabled={answered < totalQuestions || submitting}
              onClick={handleSubmit}
              data-testid="quiz-submit-btn"
            >
              {submitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('quiz_submit')
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
